import { type FC } from 'react';
import { useFragment, graphql } from 'react-relay';
import { useCurrency } from 'dibs-buyer-layout/exports/useCurrency.tsx';
import { Button, type ButtonProps } from 'dibs-elements/exports/Button.tsx';
import { getSrcsetString } from 'dibs-image-utils/exports/srcSet.ts';
import { ProductPrice } from 'dibs-buyer-product-tile/exports/ProductPrice.ts';

import { type SbSharedContentModuleStorytellingCard_storytellingModule$key } from './__generated__/SbSharedContentModuleStorytellingCard_storytellingModule.graphql.ts';

import dibsCss from 'dibs-css';
import cn from 'classnames';
import styles from './SbSharedContentModuleStorytelling.scss';
import { Link } from 'dibs-elements/exports/Link.tsx';

const storytellingCardFragment = graphql`
    fragment SbSharedContentModuleStorytellingCard_storytellingModule on StorytellingModuleItem {
        imageUrl
        headerText
        description
        footnoteText
        backgroundColor
        textColor
        includeItem
        ctaText
        ctaLink
        ctaType
        item {
            firstPhotoWebPath(size: small)
            title
            localizedPdpUrl
            ...ProductPrice_item
        }
    }
`;

type Props = {
    module: SbSharedContentModuleStorytellingCard_storytellingModule$key;
    isAlternate?: boolean;
    isMobile?: boolean;
};

export const SbSharedContentModuleStorytellingCard: FC<Props> = ({
    module: moduleRef,
    isAlternate = false,
    isMobile = false,
}) => {
    const module = useFragment(storytellingCardFragment, moduleRef);
    const { currency, showCurrency } = useCurrency();
    const {
        imageUrl,
        headerText,
        description,
        footnoteText,
        backgroundColor,
        textColor,
        includeItem,
        ctaText,
        ctaLink,
        ctaType,
        item,
    } = module;
    const buttonType = (
        ctaType === 'standardAlt' ? 'standard' : ctaType || 'primary'
    ) as ButtonProps['type'];
    const isStandardAltButton = ctaType === 'standardAlt';
    const sectionBg = backgroundColor || '#ffffff';
    const sectionTextColor = textColor || '#000000';

    return (
        <section
            className={cn({
                [styles.container]: !isMobile,
                [dibsCss.mbXsmall]: !isMobile,
                [dibsCss.mbSmall]: isMobile,
                [dibsCss.mtXsmall]: isMobile,
            })}
        >
            <div
                className={cn(dibsCss.roundedXsmall, dibsCss.overflowHidden, dibsCss.flex, {
                    [styles.alternate]: isAlternate && !isMobile,
                    [styles.storytellingCard]: !isMobile,
                    [dibsCss.flexCol]: isMobile,
                })}
            >
                <div className={cn(dibsCss.flex1)}>
                    {imageUrl && (
                        <img
                            className={cn(
                                dibsCss.block,
                                dibsCss.wFull,
                                dibsCss.hFull,
                                dibsCss.objectCover
                            )}
                            src={imageUrl}
                            alt={headerText || ''}
                            srcSet={getSrcsetString(imageUrl, [600, 800, 1200])}
                            sizes="(min-width: 768px) 50vw, 100vw"
                        />
                    )}
                </div>
                <div
                    className={cn(dibsCss.flex1)}
                    style={{ backgroundColor: sectionBg, color: sectionTextColor }}
                >
                    <div
                        className={cn(
                            dibsCss.flex,
                            dibsCss.flexCol,
                            dibsCss.justifyCenter,

                            {
                                [dibsCss.hFull]: !includeItem,
                                [styles.content]: !isMobile,
                                [dibsCss.pLarge]: !isMobile,
                                [dibsCss.pMedium]: isMobile,
                            }
                        )}
                    >
                        {headerText && (
                            <div
                                className={cn(isMobile ? dibsCss.mbXsmall : dibsCss.mbSmall, {
                                    [dibsCss.sassyFontHeaderSizeXxLarge]: !isMobile,
                                    [dibsCss.sassyFontHeaderSizeLarge]: isMobile,
                                    [styles.headerText]: !isMobile,
                                })}
                            >
                                {headerText}
                            </div>
                        )}
                        {description && (
                            <div
                                className={cn({
                                    [dibsCss.sassyFontHeaderSizeMedium]: !isMobile && headerText,
                                    [dibsCss.sassyFontHeaderSizeLarge]: !isMobile && !headerText,
                                    [dibsCss.sassyFontBodyLarge]: !includeItem && isMobile,
                                    [dibsCss.sassyFontBodyMedium]: includeItem && isMobile,
                                    [styles.descriptionText]: !isMobile,
                                })}
                                dangerouslySetInnerHTML={{ __html: description }}
                            />
                        )}
                        {footnoteText && (
                            <p
                                className={cn(
                                    dibsCss.sassyFontBodySmall,
                                    dibsCss.mtXsmall,
                                    dibsCss.opacity50
                                )}
                            >
                                {footnoteText}
                            </p>
                        )}
                        {includeItem && item && (
                            <>
                                <div
                                    className={cn({
                                        [styles.dividerWrapper]: !isMobile,
                                        [dibsCss.pyMedium]: !isMobile,
                                        [dibsCss.pySmaller]: isMobile,
                                    })}
                                >
                                    <div
                                        className={cn(styles.divider, dibsCss.h1px, dibsCss.bgNoir)}
                                    />
                                </div>

                                <div className={cn(dibsCss.flex, dibsCss.gapMedium)}>
                                    <Link
                                        underline="none"
                                        href={ctaLink || item?.localizedPdpUrl || ''}
                                    >
                                        <div>
                                            <div
                                                className={cn(
                                                    styles.itemImageWrapper,
                                                    dibsCss.flex,
                                                    dibsCss.itemsCenter,
                                                    dibsCss.justifyCenter,
                                                    dibsCss.roundedFull,
                                                    dibsCss.bgWhite,
                                                    dibsCss.overflowHidden
                                                )}
                                            >
                                                <img
                                                    src={getSrcsetString(
                                                        item?.firstPhotoWebPath || '',
                                                        [500, 800]
                                                    )}
                                                    alt={item?.title || ''}
                                                    className={cn(
                                                        styles.itemImage,
                                                        dibsCss.objectContain,
                                                        dibsCss.wFull,
                                                        dibsCss.hAuto,
                                                        dibsCss.minHFull,
                                                        dibsCss.bgWhite
                                                    )}
                                                />
                                            </div>
                                        </div>
                                    </Link>
                                    <div>
                                        <Link
                                            underline="none"
                                            href={ctaLink || item?.localizedPdpUrl || ''}
                                        >
                                            <div
                                                style={{ color: sectionTextColor }}
                                                className={cn(
                                                    dibsCss.sassyFontBodyMedium,
                                                    dibsCss.mbXxsmall,
                                                    dibsCss.lineClamp2
                                                )}
                                            >
                                                {item.title}
                                            </div>
                                            <div style={{ color: sectionTextColor }}>
                                                {showCurrency ? (
                                                    <ProductPrice item={item} currency={currency} />
                                                ) : (
                                                    <div style={{ height: '21px' }} />
                                                )}
                                            </div>
                                        </Link>
                                        <Button
                                            dataTn={`storytelling-cta`}
                                            href={ctaLink || item?.localizedPdpUrl || ''}
                                            className={cn(dibsCss.mtXsmall, {
                                                [styles.standardAltButton]: isStandardAltButton,
                                            })}
                                            size="small"
                                            type={buttonType}
                                            style="circle"
                                        >
                                            {ctaText}
                                        </Button>
                                    </div>
                                </div>
                            </>
                        )}
                    </div>
                </div>
            </div>
        </section>
    );
};
